import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { submitCaseToSF } from '../../../actions/hub/hubContactPageActions';
import ErrorBlock from '../../../common/ErrorBlock';
import { ContainerLayout } from '../../../common/hub/ContainerLayout';
import withSelectedEnrolment from '../../../common/hub/NewWithSelectedEnrolment';
import { RadioGroup, useRadioGroupProps } from '../../../components/RadioGroup/RadioGroup';
import { RestrictedButton } from '../../../components/RestrictedButton/RestrictedButton';
import { ShadowContainer } from '../../../components/ShadowContainer';
import { TextArea, useTextAreaProps } from '../../../components/TextArea/TextArea';
import FormSubHeading from '../../../components/hub/contact/FormSubHeading';
import { PATH_HUB_CR_CHANGE_TUTOR, PATH_HUB_CR_SUBMIT_CASE_SUCCESS } from '../../../util/pagePath';
import { usePageHeader } from '../../../v2/app-shell/usePageHeader';
import { StudentEnrolmentDetails } from './StudentEnrolmentDetails';

const groupName = 'contact-form-change-tutor';
const radios = [
  {
    text: 'Tutor was not the right fit',
    value: 'Tutor was not the right fit',
    key: `${groupName}-category-1`,
  },
  {
    text: 'Tutor went too slow/fast',
    value: 'Tutor went too slow/fast',
    key: `${groupName}-category-2`,
  },
  {
    text: 'Tutor is no longer available',
    value: 'Tutor is no longer available',
    key: `${groupName}-category-3`,
  },
  {
    text: 'Other',
    value: 'Other',
    key: `${groupName}-category-4`,
  },
];

const FormChangeTutor = ({ selectedEnrolment, isSubmitting, submitError, errorMsg, submit }) => {
  const { setHeader } = usePageHeader();
  const location = useLocation();
  const headerLabel = location.state?.headerLabel ?? 'Learning adjustments';

  useEffect(() => {
    setHeader({
      label: headerLabel,
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader, headerLabel]);

  const radioGroupProps = useRadioGroupProps({
    groupName,
    radios,
    labelText: 'Select a reason for changing your tutor',
  });
  const textAreaProps = useTextAreaProps({
    id: `${groupName}-textarea`,
    labelText: 'Is there anything else you’d like to tell us?',
  });

  const { enrolmentId, studentSfid, student, subject } = selectedEnrolment;
  const history = useHistory();
  const onClickHandler = () => {
    const caseType = 'Scheduling';
    const caseCategory = 'Tutor change';
    const caseSubCategory = radioGroupProps.selectedRadio;

    submit({
      uniqueEnrolmentId: enrolmentId,
      message: `Request for ${caseCategory} due to following reason: ${caseSubCategory}. Other details: ${textAreaProps.value}`,
      caseSubject: `Contact request - ${caseType} - ${caseCategory} - ${caseSubCategory}`,
      caseType,
      caseCategory,
      caseSubCategory,
      onSubmitSuccess: () => {
        history.push(PATH_HUB_CR_SUBMIT_CASE_SUCCESS, {
          studentId: studentSfid,
          firstName: student,
          message: `We will be in touch to discuss your request and set up your changes.`,
        });
      },
    });
  };

  return (
    <ContainerLayout>
      <ShadowContainer className="flex flex-col gap-y-10 !rounded-3xl bg-white p-6 md:!p-20">
        <FormSubHeading />
        {submitError && errorMsg && <ErrorBlock errorMsg={errorMsg} />}
        <StudentEnrolmentDetails student={student} subject={subject} selectedEnrolment={selectedEnrolment} />
        <form className="flex flex-col gap-y-10">
          <RadioGroup {...radioGroupProps} />
          <TextArea {...textAreaProps} />
          <div className="mb-4 text-center tracking-normal lg:mb-8">
            <RestrictedButton
              buttonText="Request change"
              truthyChecks={[!!radioGroupProps.selectedRadio, !isSubmitting]}
              onClick={onClickHandler}
            />
          </div>
        </form>
      </ShadowContainer>
    </ContainerLayout>
  );
};

FormChangeTutor.propTypes = {
  selectedEnrolment: PropTypes.shape({
    enrolmentId: PropTypes.string.isRequired,
    studentSfid: PropTypes.string.isRequired,
    student: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitError: PropTypes.bool.isRequired,
  errorMsg: PropTypes.string.isRequired,
  submit: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    return {
      isSubmitting: state.ui.apiState.hubContactPage.isLoading,
      submitError: state.ui.apiState.hubContactPage.fetchError,
      errorMsg: state.ui.apiState.hubContactPage.errorMsg,
    };
  },
  (dispatch) => {
    return {
      submit: bindActionCreators(submitCaseToSF, dispatch),
    };
  }
)(withSelectedEnrolment(FormChangeTutor, null, PATH_HUB_CR_CHANGE_TUTOR));

export { FormChangeTutor as StatelessFormChangeTutor };
